import { useEffect, useId, useRef, useState } from "react";
import { request } from "../api/client";
import { FieldRequirement } from "../components/FieldRequirement";
import { useLocale } from "../i18n";
import "../styles/team-logo.css";

type Policy = { maxBytes: number; maxFiles: number; contentTypes: string[] };
type Upload = { fileId: string; uploadUrl: string; method: string; fields: Record<string, string>; ready?: boolean };
type Attempt = { file: File; requestId: string; fileId?: string; uploaded: boolean };
export function TeamLogoUpload({ unitId, value, onChange, onStatusChange, validationError }: {
  unitId: string; value?: string; onChange: (id?: string) => void;
  onStatusChange: (status: { busy: boolean; blocked: boolean }) => void; validationError?: string;
}) {
  const { w } = useLocale(); const id = useId();
  const [policy, setPolicy] = useState<Policy>(); const [policyError, setPolicyError] = useState(false);
  const [reload, setReload] = useState(0); const [busy, setBusy] = useState(false);
  const [error, setError] = useState<"invalid" | "upload">(); const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const attempt = useRef<Attempt | undefined>(undefined); const running = useRef(false); const input = useRef<HTMLInputElement>(null);
  const active = useRef(new AbortController()); const change = useRef(onChange); change.current = onChange;
  const status = useRef(onStatusChange); status.current = onStatusChange;
  const path = "/events/partyGroup/logoUpload/";
  const query = "?" + new URLSearchParams({ unitId });
  useEffect(() => {
    const controller = new AbortController(); setPolicyError(false);
    request<Policy>(path + "options" + query, "GET", undefined, controller.signal)
      .then(result => { if (!controller.signal.aborted) {
        if (!(result.maxBytes > 0) || result.maxFiles !== 1 || !result.contentTypes?.length) throw new Error("Invalid upload policy");
        setPolicy(result);
      } }).catch(() => { if (!controller.signal.aborted) setPolicyError(true); });
    return () => controller.abort();
  }, [query, reload]);
  useEffect(() => { const controller = new AbortController(); active.current = controller; return () => controller.abort(); }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  useEffect(() => { status.current({ busy, blocked: busy || !!error }); }, [busy, error]);
  useEffect(() => () => status.current({ busy: false, blocked: false }), []);
  const help = policy
    ? w(`ارفع صورة واحدة بصيغة JPG أو PNG أو WebP أو GIF، بحد أقصى ${policy.maxBytes / 1048576} ميغابايت.`, `Upload one JPG, PNG, WebP or GIF image, up to ${policy.maxBytes / 1048576} MB.`)
    : w("جارٍ تحميل شروط رفع صورة واحدة…", "Loading single-image upload requirements…");
  const message = error === "invalid" ? w("اختر صورة واحدة بالصيغ والحجم الموضحين. يمكنك إزالة الصورة للمتابعة دون شعار.", "Choose one image within the listed formats and size, or remove it to continue without a logo.")
    : error === "upload" ? w("تعذّر إكمال الرفع. أعد المحاولة بالصورة نفسها أو أزلها للمتابعة دون شعار.", "Upload could not be completed. Retry with the same image or remove it to continue without a logo.") : validationError;
  async function upload(current: Attempt) {
    if (running.current) return; running.current = true; setBusy(true); setError(undefined);
    const signal = active.current.signal;
    try {
      if (!current.uploaded) {
        const result = await request<Upload>(path + "prepare" + query, "POST", {
          requestId: current.requestId, contentType: current.file.type, contentLength: current.file.size,
        }, signal);
        current.fileId = result.fileId;
        if (!result.ready) {
          const url = new URL(result.uploadUrl);
          if (url.protocol !== "https:" || url.username || url.password || result.method !== "POST") throw new Error("Invalid upload destination");
          const data = new FormData();
          for (const [key, value] of Object.entries(result.fields)) data.append(key, value);
          data.append("file", current.file);
          const response = await fetch(url, { method: "POST", body: data, credentials: "omit", signal: AbortSignal.any([signal, AbortSignal.timeout(120000)]) });
          if (!response.ok) throw new Error("Upload failed");
        }
        current.uploaded = true;
      }
      const completed = await request<{ fileId: string; ready: boolean }>(path + "complete" + query, "POST", { fileId: current.fileId }, signal);
      if (!completed.ready || completed.fileId !== current.fileId) throw new Error("Image is not ready");
      if (!signal.aborted) change.current(completed.fileId);
    } catch { if (!signal.aborted) setError("upload"); }
    finally { running.current = false; if (!signal.aborted) setBusy(false); }
  }
  function select(files: FileList | null) {
    if (running.current) return;
    change.current(undefined); attempt.current = undefined; setError(undefined); setPreview("");
    const file = files?.[0]; setFileName(file?.name || "");
    if (!file) return;
    if (!policy || files?.length !== 1 || !policy.contentTypes.includes(file.type) || file.size < 1 || file.size > policy.maxBytes) { setError("invalid"); return; }
    setPreview(URL.createObjectURL(file));
    const current = { file, requestId: crypto.randomUUID(), uploaded: false }; attempt.current = current; void upload(current);
  }
  return <div className="event-field team-logo-field" data-field-path="logoMediaId">
    <label htmlFor={id}>{w("شعار الفريق", "Team logo")} <FieldRequirement required={false} /></label>
    <input ref={input} id={id} type="file" accept={policy?.contentTypes.join(",") || "image/jpeg,image/png,image/webp,image/gif"}
      disabled={busy || !policy} aria-describedby={`${id}-help${message ? ` ${id}-error` : ""}`} aria-invalid={!!message}
      onChange={e => select(e.currentTarget.files)} />
    <small id={`${id}-help`} className="event-field-help">{help}</small>
    {policyError && <div><span role="alert">{w("تعذّر تحميل شروط رفع الشعار.", "Could not load logo upload requirements.")}</span><button type="button" className="text-button" onClick={() => setReload(n => n + 1)}>{w("إعادة المحاولة", "Try again")}</button></div>}
    {preview && <img className="team-logo-preview" src={preview} alt={w("معاينة شعار الفريق", "Team logo preview")} />}
    {busy && <small role="status">{w("جارٍ رفع الصورة والتحقق منها…", "Uploading and verifying the image…")}</small>}
    {!busy && value && <small role="status">{w("الشعار جاهز للحفظ مع الفريق.", "Logo ready to save with the team.")}</small>}
    {message && <small id={`${id}-error`} className="event-field-error" role="alert">{message}</small>}
    {error === "upload" && attempt.current && <button type="button" className="secondary" disabled={busy} onClick={() => void upload(attempt.current!)}>{w("إعادة رفع الشعار", "Retry logo upload")}</button>}
    {(fileName || value) && <button type="button" className="text-button" disabled={busy} onClick={() => { attempt.current = undefined; change.current(undefined); setPreview(""); setFileName(""); setError(undefined); if (input.current) input.current.value = ""; }}>{w("إزالة الشعار", "Remove logo")}</button>}
  </div>;
}
